import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import * as turf from '@turf/turf'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import AppContext from '../../context/appContext'
import { useMonitoringEvents, useRemoveMonitoringEvent } from '../../hooks/data/useMonitoringEvents'
import { useMonitoringSites } from '../../hooks/data/useMonitoringSites'
import { useMonitoringWorkflows } from '../../hooks/data/useMonitoringWorkflows'
import { useCroppedSamplingAreas } from '../../hooks/data/useSamplingAreas'
import { useUserRole } from '../../hooks/useUserRole'
import { GeoJSON } from '../../utils/GeoJSON/index'
import { useProcessLoadingWithConfirmation } from '../../utils/Loading/useProcessLoadingWithConfirmation'
import CreateEventButton from './CreateEventButton'
import CreateEventDialog from './CreateEventWithSteps/CreateEventDialog'
import EventMap from './EventMap'
import EventPanel from './EventPanel'
import EventsList from './EventsList'

// Hoisted so the fallback keeps one identity across renders: EventScopeSelection keys an
// effect on this list.
const emptyMonitoringSites = []

const EventPlanning = () => {
  const { currentFarm, setLoadingModalConfig } = useContext(AppContext)
  const { data: monitoringSites = emptyMonitoringSites } = useMonitoringSites({
    farmId: currentFarm?.id,
    isMonitoringOnly: false,
  })
  // No `= []` default on the queries below: it would collapse loading, error and genuinely-empty
  // into the same truthy value, and EventsList reads an empty array as "loaded, no events".
  const { data: monitoringEvents, isError: eventsError } = useMonitoringEvents(currentFarm?.id)
  const { data: croppedAreas, isError: samplingAreasError } = useCroppedSamplingAreas({
    farmId: currentFarm?.id,
  })
  // Gated on the farm so a farmless screen shows the no-farm state, never a workflows error. The
  // error is gated too: a disabled query still reports its cached error, and EventsList renders
  // errors before the no-farm state.
  const {
    data: monitoringWorkflows,
    isError: workflowsError,
    refetch: refetchWorkflows,
  } = useMonitoringWorkflows({ enabled: !!currentFarm?.id })
  const workflowsLoadError = workflowsError && !!currentFarm?.id
  const removeMonitoringEvent = useRemoveMonitoringEvent(currentFarm?.id)
  const [mapWidth] = useState(window.innerWidth / 2)
  const [mapHeight] = useState(window.innerHeight)
  const [selectedMonitoringSites, setSelectedMonitoringSites] = useState([])
  const [selectedSamplingAreas, setSelectedSamplingAreas] = useState([])
  const [createEventView, setCreateEventView] = useState(false)
  const { isViewOnly, isAdmin } = useUserRole()
  const { processLoadingWithConfirmation } = useProcessLoadingWithConfirmation()

  const [createEventWithGpxView, setCreateEventWithGpxView] = useState(false)
  const createEventOptions = useMemo(() => {
    if (isAdmin) {
      return [
        {
          label: 'Crear evento',
          action: () => setCreateEventView(true),
        },
        {
          label: 'Crear evento histórico',
          action: () => setCreateEventWithGpxView(true),
        },
      ]
    }
    return [
      {
        label: 'Crear evento',
        action: () => setCreateEventView(true),
      },
    ]
  }, [isAdmin])

  const handleDeleteEvent = async eventId => {
    await processLoadingWithConfirmation({
      confirmationTitle: 'Confirmar eliminación de evento',
      confirmationMessage: '¿Estás seguro que deseas eliminar el evento?',
      loadingMessage: 'Eliminando evento...',
      errorMessage: 'Error al eliminar el evento',
      setLoadingModalConfig,
      successfulMessage: 'Evento eliminado',
      // The mutation invalidates the events query on success, so the list refreshes itself.
      doAction: async ({ dryRun }) => {
        if (dryRun) return
        await removeMonitoringEvent.mutateAsync(eventId)
      },
    })
  }

  // The workflows key is global, so a farm switch does not refetch it: retry a failed read here so
  // the error does not follow the user to every farm until a remount.
  const lastFarmId = useRef(currentFarm?.id)
  useEffect(() => {
    if (lastFarmId.current === currentFarm?.id) return
    lastFarmId.current = currentFarm?.id
    if (workflowsError && currentFarm?.id) refetchWorkflows()
  }, [currentFarm?.id, workflowsError, refetchWorkflows])

  // appContext defaults currentFarm to {}, which is truthy: only a farm with an id is a selection.
  const farm = currentFarm?.id ? currentFarm : undefined
  const perimeter = farm?.toGeoJSON

  // The hook hands every observer its own clone, so stamping the computed area onto each
  // feature stays local to this component and re-running the derivation is idempotent.
  const samplingAreasFeatureCollection = useMemo(() => {
    if (!croppedAreas) return undefined

    const samplingAreasFeatures = turf.featureCollection([])
    croppedAreas.forEach(samplingArea => {
      const feature = samplingArea.toGeoJSON
      feature.properties.area = GeoJSON.hectareArea(feature).toFixed(2)
      samplingAreasFeatures.features.push(feature)
    })
    return samplingAreasFeatures
  }, [croppedAreas])

  const events = useMemo(
    () =>
      monitoringEvents?.map(monitoringEvent => ({
        ...monitoringEvent,
        samplingAreas: (croppedAreas ?? []).filter(samplingArea =>
          monitoringEvent.samplingAreas?.includes(samplingArea.id),
        ),
        monitoringSites: monitoringSites.filter(monitoringSite =>
          monitoringEvent.monitoringSitesIds?.includes(monitoringSite.id),
        ),
      })),
    [monitoringEvents, croppedAreas, monitoringSites],
  )

  return (
    <Box>
      <Box sx={{ padding: '10px', textAlign: 'right' }}>
        {!createEventView && createEventOptions && createEventOptions.length > 0 && (
          <CreateEventButton disabled={isViewOnly} options={createEventOptions} />
        )}
      </Box>
      {!createEventView && (
        <EventsList
          events={events}
          eventsError={eventsError}
          farm={farm}
          handleDeleteEvent={handleDeleteEvent}
          loadError={workflowsLoadError || samplingAreasError}
          monitoringWorkflows={monitoringWorkflows}
          noFarmSelected={!currentFarm?.id}
        />
      )}
      {createEventView && (
        <Grid container>
          <Grid item xs={6}>
            <EventPanel
              farm={farm}
              monitoringSites={monitoringSites}
              monitoringWorkflows={monitoringWorkflows}
              samplingAreas={samplingAreasFeatureCollection}
              selectedMonitoringSites={selectedMonitoringSites}
              selectedSamplingAreas={selectedSamplingAreas}
              setCreateEventView={setCreateEventView}
              setSelectedMonitoringSites={setSelectedMonitoringSites}
              setSelectedSamplingAreas={setSelectedSamplingAreas}
            />
          </Grid>
          <Grid item xs={6}>
            <EventMap
              mapHeight={mapHeight}
              mapWidth={mapWidth}
              monitoringSites={monitoringSites}
              perimeter={perimeter}
              samplingAreas={samplingAreasFeatureCollection}
              selectedMonitoringSites={selectedMonitoringSites}
              selectedSamplingAreas={selectedSamplingAreas}
            />
          </Grid>
        </Grid>
      )}
      <CreateEventDialog
        handleClose={() => setCreateEventWithGpxView(false)}
        monitoringWorkflows={monitoringWorkflows}
        open={createEventWithGpxView}
      />
    </Box>
  )
}

export default EventPlanning
